import React, { useState, useEffect, useRef } from 'react';
import { loadOrCreateIdentity, deriveBoxKeyPair, arrayBufferToBase64 } from '../lib/identity';
import { getProfile } from '../lib/metadata';
import { getConversations, getConversation, markAsRead, encryptDM, saveOwnSentDM, addMessageListener, getBoxPubkey, deleteConversation } from '../lib/messages';
import { publishEvent } from '../lib/sync';
import { COLORS, SPACING } from '../styles/theme';

export function DirectMessages({ onBack, startConversationWith }: { onBack: () => void; startConversationWith?: { userId: string; username: string; boxPubkey?: string } }) {
  const [conversations, setConversations] = useState<ReturnType<typeof getConversations>>([]);
  const [selectedOtherId, setSelectedOtherId] = useState<string | null>(startConversationWith?.userId ?? null);
  const [selectedOtherUsername, setSelectedOtherUsername] = useState<string>(startConversationWith?.username ?? '');
  const [selectedOtherBoxPubkey, setSelectedOtherBoxPubkey] = useState<string | null>(startConversationWith?.boxPubkey ?? null);
  const [messages, setMessages] = useState<ReturnType<typeof getConversation>>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const identity = useRef(loadOrCreateIdentity()).current;
  const profile = useRef(getProfile()).current;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const refresh = () => {
    setConversations(getConversations(identity.userId));
    if (selectedOtherId) {
      setMessages(getConversation(identity.userId, selectedOtherId));
    }
  };

  useEffect(() => {
    refresh();
    return addMessageListener(refresh);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOtherId) {
      setMessages(getConversation(identity.userId, selectedOtherId));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOtherId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectConversation = (otherId: string, otherUsername: string, boxPubkey?: string) => {
    setSelectedOtherId(otherId);
    setSelectedOtherUsername(otherUsername);
    const key = boxPubkey ?? getBoxPubkey(otherId);
    if (key) setSelectedOtherBoxPubkey(key);
    markAsRead(identity.userId, otherId);
    setConversations(getConversations(identity.userId));
  };

  const handleDeleteConversation = (e: React.MouseEvent, otherId: string) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation? This only removes it from your device.')) return;
    deleteConversation(identity.userId, otherId);
    if (selectedOtherId === otherId) {
      setSelectedOtherId(null);
      setMessages([]);
    }
    setConversations(getConversations(identity.userId));
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedOtherId || sending) return;
    if (!selectedOtherBoxPubkey) {
      alert('Cannot send: recipient encryption key not found. Ask them to message you first to exchange keys.');
      return;
    }
    setSending(true);
    const text = newMessage.trim();
    setNewMessage('');
    try {
      const encrypted = encryptDM(text, selectedOtherBoxPubkey, identity);
      const event = await publishEvent('dm', {
        toUserId: selectedOtherId,
        fromUserId: identity.userId,
        fromUsername: profile?.username ?? identity.userId,
        fromBoxPubkey: encrypted.fromBoxPubkey,
        nonce: encrypted.nonce,
        ciphertext: encrypted.ciphertext,
      });
      saveOwnSentDM({
        id: event.id,
        fromUserId: identity.userId,
        fromUsername: profile?.username ?? identity.userId,
        toUserId: selectedOtherId,
        text,
        createdAt: event.timestamp,
      });
      setMessages(getConversation(identity.userId, selectedOtherId));
    } finally {
      setSending(false);
    }
  };

  const myBoxPubkey = arrayBufferToBase64(deriveBoxKeyPair(identity).publicKey);

  return (
    <div style={{
      border: `2px outset ${COLORS.highlight}`,
      backgroundColor: COLORS.panel,
      overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: '200px 1fr',
      height: '100%'
    }}>
      {/* Sidebar */}
      <div style={{ borderRight: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column' }}>
        <div style={{
          backgroundColor: COLORS.accent, color: '#fff',
          padding: `${SPACING.xs} ${SPACING.sm}`,
          fontSize: '9px', fontWeight: 700,
          borderBottom: `2px solid ${COLORS.highlight}`,
          userSelect: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING.xs,
        }}>
          <span>Messages</span>
          <span title="End-to-end encrypted. Nobody — including Authenticulture admins — can read your DMs." style={{ fontSize: '8px', fontWeight: 400, opacity: 0.85, cursor: 'help', whiteSpace: 'nowrap' }}>
            🔒 E2E encrypted
          </span>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', backgroundColor: COLORS.bg }}>
          {conversations.length === 0 ? (
            <p style={{ padding: SPACING.lg, color: COLORS.textMuted, margin: 0, fontSize: '9px' }}>
              No conversations yet
            </p>
          ) : (
            conversations.map(conv => (
              <div key={conv.otherId} style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${COLORS.border}` }}>
                <button
                  onClick={() => selectConversation(conv.otherId, conv.otherUsername)}
                  style={{
                    display: 'block', flex: 1, padding: SPACING.md,
                    backgroundColor: selectedOtherId === conv.otherId ? COLORS.accent : COLORS.bg,
                    color: selectedOtherId === conv.otherId ? '#fff' : COLORS.text,
                    border: 'none',
                    cursor: 'pointer', textAlign: 'left',
                    fontSize: '9px', fontWeight: 700, fontFamily: 'monospace',
                    textDecoration: 'underline',
                  }}
                >
                  {conv.otherUsername || conv.otherId}
                  {conv.unread > 0 && <span style={{ marginLeft: 4, color: COLORS.accent }}>({conv.unread})</span>}
                </button>
                <button
                  onClick={(e) => handleDeleteConversation(e, conv.otherId)}
                  title="Delete conversation"
                  style={{
                    background: selectedOtherId === conv.otherId ? COLORS.accent : COLORS.bg,
                    color: COLORS.danger, border: 'none', cursor: 'pointer',
                    padding: `0 ${SPACING.md}`, fontSize: '11px', fontFamily: 'monospace',
                  }}
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>
        <div style={{ padding: SPACING.sm, borderTop: `1px solid ${COLORS.border}`, fontSize: '8px', color: COLORS.textMuted, wordBreak: 'break-all' }}>
          Your key: {myBoxPubkey.slice(0, 12)}…
        </div>
      </div>

      {/* Thread */}
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedOtherId ? (
          <>
            <div style={{
              backgroundColor: COLORS.accent, color: '#fff',
              padding: `${SPACING.xs} ${SPACING.sm}`,
              fontSize: '9px', fontWeight: 700,
              borderBottom: `2px solid ${COLORS.highlight}`,
              userSelect: 'none'
            }}>
              {selectedOtherUsername || selectedOtherId}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', backgroundColor: COLORS.bg, padding: SPACING.lg }}>
              {messages.length === 0 && (
                <p style={{ color: COLORS.textMuted, fontSize: '9px', margin: 0 }}>
                  {selectedOtherBoxPubkey
                    ? 'No messages yet. Say hello.'
                    : 'Waiting for this user to message you first — needed to establish their encryption key.'}
                </p>
              )}
              {messages.map(msg => {
                const isOwn = msg.fromUserId === identity.userId;
                return (
                  <div key={msg.id} style={{ marginBottom: SPACING.lg, display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '70%', padding: SPACING.md,
                      backgroundColor: isOwn ? COLORS.accent : COLORS.panel,
                      color: isOwn ? '#fff' : COLORS.text,
                      border: `1px solid ${isOwn ? COLORS.accent : COLORS.border}`,
                      fontSize: '10px', lineHeight: 1.4
                    }}>
                      {msg.text}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ padding: SPACING.lg, borderTop: `1px solid ${COLORS.border}`, backgroundColor: COLORS.panel, display: 'flex', gap: SPACING.sm }}>
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }}
                placeholder="Message..."
                style={{
                  flex: 1, padding: SPACING.md,
                  backgroundColor: '#fff', color: '#000',
                  border: `2px inset ${COLORS.highlight}`,
                  fontFamily: 'monospace', fontSize: '10px'
                }}
              />
              <button
                onClick={handleSend}
                disabled={!newMessage.trim() || sending}
                style={{
                  padding: `${SPACING.md} ${SPACING.lg}`,
                  backgroundColor: newMessage.trim() ? COLORS.accent : COLORS.panel,
                  color: newMessage.trim() ? '#fff' : COLORS.textMuted,
                  border: `2px outset ${newMessage.trim() ? COLORS.accent : COLORS.border}`,
                  cursor: newMessage.trim() ? 'pointer' : 'default',
                  fontFamily: 'monospace', fontSize: '9px', fontWeight: 700,
                  textDecoration: 'underline',
                }}
              >
                {sending ? '...' : 'Send'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: COLORS.textMuted, fontSize: '10px' }}>
            Select a conversation
          </div>
        )}
      </div>
    </div>
  );
}
